/**
 * IndexedDB de la app (docs/esquema.md §5.1, v3). Stores:
 * - config:     registros chicos por clave ('sesion' | 'cola' | 'enrolamiento' | 'preferencias' |
 *               'arranque' (v6, §12.7: el arranque al que pertenece lo local) | 'avisos_entendidos' |
 *               'caidas': ids de mutaciones que el servidor no aceptó, para que un hijo
 *               confirmado tarde no viaje huérfano)
 * - catalogos:  el pull completo bajo la clave 'catalogos'
 * - outbox:     mutaciones por id, CADA UNA con su {epoch, seq} (una fila jamás
 *               se re-etiqueta; los metadatos de cola viven en config.cola),
 *               tipadas por entidad (viaje de verde o descarte de recepción)
 * - recibos:    registros ya sincronizados, por RAÍZ (una corrección reemplaza
 *               el recibo de su raíz en vez de sumarse), retención limitada
 * - borradores: capturas a medias, por capture_id
 *
 * Sin cola de fotos: verde no carga ninguna. El esquema local es la versión
 * inicial —no hay instalaciones reales que migrar—; una instalación de prueba
 * vieja se borra a mano.
 */

import type { PalletAbiertoServidor, RotuloDelCatalogo } from '../nucleo/embolsado';
import type { IntervaloEnResumen } from '../nucleo/produccion';

export const NOMBRE_BD = 'perejil';
/** 2 (14/9): suma el store `fotos` (la foto del precinto, §5.3). */
const VERSION_BD = 2;

export interface Sesion {
	token: string;
	dispositivo_id: string;
	estacion: string | null;
	/** v4.1 (7/9): la persona del teléfono personal; null en el muleto. Deja el «¿quién?» contestado. */
	persona_habitual_id: string | null;
	nombre_dispositivo: string;
	enrolado_en: string;
}

export interface MetadatosCola {
	epoch: string;
	proximo_seq: number;
}

export interface EnrolamientoPendiente {
	request_id: string;
	codigo: string;
	nombre: string;
}

export interface Preferencias {
	ultimo_lote_id?: string;
	/** Calidad, en el muleto: la última analista que cargó, para premarcarla (boceto 7/9). */
	ultima_analista_id?: string;
	desvio_ms?: number;
	hora_ultimo_sync?: string;
	persistencia_ok?: boolean;
	desenrolado?: boolean;
	catalogos_bajados_en?: number;
	/** Epochs retenidos tras un 401: solo el encargado los libera (H4 de la ronda). */
	epochs_congelados?: string[];
	/** Último error técnico de sync (400 del server, pendiente anómalo): visible en config. */
	ultimo_error_sync?: string;
	/** Un acuse inesperado al subir una foto (portal cautivo, bug del server): visible en config. */
	ultimo_error_fotos?: string;
	/**
	 * `hora_servidor` del snapshot de catálogos que está guardado. Es el reloj DEL
	 * SERVIDOR, el mismo con el que se fechan los acuses: comparar contra él dice si
	 * el catálogo ya incluye un efecto confirmado o si todavía es más viejo que él.
	 */
	catalogos_hora_servidor?: string;
	/** Un refresco forzado de catálogos que no entró (§5.1): se vuelve a pedir en la pasada siguiente aunque los locales sean recientes. */
	catalogos_por_refrescar?: boolean;
}

export type Entidad = 'viaje_verde' | 'descarte' | 'llenado' | 'parcial' | 'cambio_carro' | 'parada' | 'muestra' | 'pallet' | 'envasado' | 'aviso' | 'nota_llenado';

/** Las de la estación producción: las que alimentan la proyección de silos abiertos. */
export const ENTIDADES_PRODUCCION = new Set<Entidad>(['llenado', 'parcial', 'cambio_carro', 'parada']);
/**
 * Las que se PROYECTAN sobre el catálogo (§5.1): producción sobre los silos abiertos
 * y la muestra sobre los silos que esperan muestra. Sus recibos guardan el comando
 * aplicado mientras el snapshot no lo refleje, y no se purgan antes.
 */
export const ENTIDADES_PROYECTADAS = new Set<Entidad>([...ENTIDADES_PRODUCCION, 'muestra', 'pallet', 'envasado', 'aviso', 'nota_llenado', 'viaje_verde']);
/** v6 (§12.2, §12.4): los avisos y las notas, proyectados sobre `avisos_turno` y `notas_llenado`. */
export const ENTIDADES_AVISOS = new Set<Entidad>(['aviso', 'nota_llenado']);
/** v5 (§5.7): las de la estación envasado, proyectadas por identidad sobre `pallets_abiertos`. */
export const ENTIDADES_EMBOLSADO = new Set<Entidad>(['pallet', 'envasado']);

export interface ResumenViaje {
	entidad: 'viaje_verde';
	fecha: string;
	/** La hora declarada de entrada (§5.5, verde reciente): ordena y recorta lo propio en «Verde, últimas 24 horas». Un resumen guardado antes no la trae. */
	entrada_en?: string;
	lote: string;
	carros: string;
	cant_carros: number;
	bruto_g: number;
	neto_g: number;
}

export interface ResumenDescarte {
	entidad: 'descarte';
	fecha: string;
	/** La hora declarada del descarte (§5.5, verde reciente). Un resumen guardado antes no la trae. */
	registrado_en?: string;
	neto_g: number;
}

/** v4 · producción: las tres transiciones del silo comparten resumen; la acción las distingue. */
export interface ResumenLlenado {
	entidad: 'llenado';
	fecha: string;
	accion: 'abrio' | 'cerro' | 'descarto';
	silo: string;
	precinto: string;
	/** Neto conocido al momento de cerrar; null al abrir y en un silo sin pesar. */
	neto_g: number | null;
	/**
	 * Dónde y desde cuándo estuvo el silo (2/10). El recibo es uno por silo y el acuse del cierre
	 * pisa al de la apertura: sin esto, el teléfono olvidaba el trieur y la hora de apertura de un
	 * silo cerrado y no podía avisar el choque al abrir otro (Codex, ronda del diff, 2/10). Lo que
	 * se encoló antes no lo trae.
	 */
	intervalo?: IntervaloEnResumen;
}


export interface ResumenParcial {
	entidad: 'parcial';
	fecha: string;
	precinto: string;
	bruto_g: number;
	/** Lo que entró en ESTE turno: bruto menos el anterior. */
	del_turno_g: number;
}

export interface ResumenCambioCarro {
	entidad: 'cambio_carro';
	fecha: string;
	carro: string;
}

export interface ResumenParada {
	entidad: 'parada';
	fecha: string;
	motivo: string;
	minutos: number;
}

/** Calidad: la muestra de un silo con su letra. El número lo pone el servidor y llega en el acuse. */
export interface ResumenMuestra {
	entidad: 'muestra';
	fecha: string;
	silo: string;
	precinto: string;
	letra: 'Q' | 'P' | 'L' | 'descarte';
	/**
	 * v6: qué se hizo con la muestra; sin acción es la hoja que tomó calidad. `cambio_letra`: envasado le
	 * cambió la calidad (§12.5); `humedad`: calidad la completó (§12.9); `humedad_corregida`: calidad la
	 * corrigió (§12.17); `hoja_corregida`: calidad corrigió la hoja entera (§12.18). «Lo cargado» los cuenta aparte de las
	 * muestras tomadas. Lo encolado antes no lo trae.
	 */
	accion?: 'cambio_letra' | 'humedad' | 'humedad_corregida' | 'hoja_corregida';
	/** §12.17: en una corrección, la humedad que se puso y la que había (la bandeja las muestra para rehacerla). */
	humedad?: number;
	humedad_antes?: number;
	/** §12.17: lo que necesita la tarjeta de «Humedad cargada · últimas 24 h» (ver `TarjetaDeHumedad`). */
	tarjeta?: import('../nucleo/muestra').TarjetaDeHumedad;
	/** §12.18: en una hoja o una corrección de la hoja, cómo queda la hoja («Cargadas · últimas 48 h» la muestra sin señal). */
	hoja?: import('../nucleo/muestra').TarjetaDeHoja;
	/** §12.18: en una corrección de la hoja, lo que cambió en palabras y la letra que tenía. */
	cambios?: string[];
	letra_antes?: 'Q' | 'P' | 'L' | 'descarte';
}

/** v5 · embolsado: el pallet o la tarima de espera; la acción distingue la transición. El número llega en el acuse de la apertura. */
export interface ResumenPallet {
	entidad: 'pallet';
	fecha: string;
	accion: 'abrio' | 'cerro' | 'anulo' | 'unio' | 'corrigio';
	es_tarima: boolean;
	letra: 'Q' | 'P' | 'L' | 'D' | null;
	formato_bolsa_g: number;
	/** Las bolsas al cerrar (el testigo), o las que pasaron al pallet al unir una tarima (§12.21); null en las demás transiciones. */
	bolsas: number | null;
	/** El número del rótulo del pallet (r. 22): al abrir es el elegido; al cerrar uno del catálogo, el recibo del cierre lo hereda aunque no haya recibo de la apertura. Null en la tarima. */
	numero?: number | null;
	/** §12.21, al unir de a partes: cuántas quedaron en espera (0 si la tarima terminó), y el número del pallet que recibió. */
	quedan?: number | null;
	destino_numero?: number | null;
}

/** v5 · embolsado: N bolsas de un precinto en un pallet o una tarima. */
export interface ResumenEnvasado {
	entidad: 'envasado';
	fecha: string;
	accion: 'cargo' | 'corrigio' | 'anulo';
	precinto: string;
	bolsas: number;
	/** «pallet Nº 241 · Q · 7,25 kg» (desde el 15/10, §12.18; antes sin el número) o «tarima de espera»: cómo se nombra el destino en el reporte. */
	destino: string;
}

/** v6 (§12.2): un aviso dejado (o anulado) para las demás encargadas. */
export interface ResumenAviso {
	entidad: 'aviso';
	fecha: string;
	accion: 'dejo' | 'anulo';
	texto: string;
}

/** v6 (§12.4): una nota a un silo (o su anulación). */
export interface ResumenNota {
	entidad: 'nota_llenado';
	fecha: string;
	accion: 'escribio' | 'anulo';
	precinto: string;
	texto: string;
}

export type Resumen =
	| ResumenViaje
	| ResumenDescarte
	| ResumenLlenado
	| ResumenParcial
	| ResumenCambioCarro
	| ResumenParada
	| ResumenMuestra
	| ResumenPallet
	| ResumenEnvasado
	| ResumenAviso
	| ResumenNota;

export interface FilaOutbox {
	id: string;
	epoch: string;
	seq: number;
	tipo: string;
	payload: unknown;
	creado_en: string;
	entidad: Entidad;
	/** Id del registro que la mutación crea o corrige (viaje o descarte). */
	registro_id: string;
	/** Quién cargó, para el reporte «cargados desde este teléfono». */
	personas: string[];
	/**
	 * v4 (§5.2, regla 14): el registro del que este depende y que se creó en ESTE
	 * teléfono — el `llenado_id` de un parcial. Mientras el padre siga pendiente en
	 * la cola, el hijo no viaja: sin el silo en el servidor no hay dónde colgarlo.
	 */
	padre_id?: string;
	estado_local: 'pendiente' | 'rechazada' | 'conflicto';
	motivo: string | null;
	resumen: Resumen;
}

export interface Recibo {
	/** Identidad lógica del registro: el acuse del servidor la trae (v3). */
	raiz_id: string;
	entidad: Entidad;
	registro_id: string;
	/**
	 * v4 · producción: el comando tal como se aplicó. La proyección de silos abiertos
	 * lo necesita mientras el catálogo sea más viejo que este acuse — si no, un silo
	 * recién abierto DESAPARECE de la pantalla en cuanto llega su propio ACK, hasta
	 * el refresco siguiente (Codex, ronda del plan, 8/9).
	 */
	tipo?: string;
	payload?: unknown;
	nro_viaje: number | null;
	/** Calidad: el número de muestra por campaña que asignó el servidor (r. 67). */
	nro_muestra?: number | null;
	/**
	 * v5 · embolsado: el número del pallet por campaña, que trae el acuse de `pallet.abrir` y las
	 * transiciones siguientes conservan (§5.7 r. 20): es lo que va al rótulo.
	 */
	numero?: number;
	/**
	 * v6 · muestra: el teléfono guarda un recibo por raíz, así que el acuse de una versión pisa al de
	 * la anterior. Si el catálogo todavía muestra una de esas anteriores, `cambiosDeLetra` necesita
	 * el puente hasta la última: acá van las versiones que este recibo reemplazó y la última letra
	 * conocida de la cadena (5/10, ronda de disensor).
	 */
	cadena?: CadenaDeMuestra;
	personas: string[];
	resumen: Resumen;
	sincronizado_en: string;
}

/** Las versiones de una muestra que el acuse de la última pisó, y la última letra elegida en ellas. */
export interface CadenaDeMuestra {
	bases: string[];
	letra: string | null;
	/**
	 * §12.17: la jornada de la hoja, si la tomó este teléfono y su recibo lo pisó una versión siguiente (completar o
	 * corregir la humedad). «Muestreadas hoy» y «Lo cargado» la siguen contando como muestra.
	 */
	hoja?: string;
}

export type MotivoFallaFoto =
	/** 404: el silo no existe en el servidor (la apertura nunca entró, o el teléfono se re-enroló). */
	| 'entidad_inexistente'
	/** 409: ese `foto_id` ya se usó con otro archivo. */
	| 'foto_resuelta'
	/** 403: este teléfono no puede subir fotos de esta entidad. */
	| 'estacion_no_autorizada'
	/** 413: el servidor no acepta un binario tan grande. */
	| 'demasiado_grande'
	/** 411: el envío salió sin declarar su tamaño; el navegador siempre lo declara: es un problema de la app. */
	| 'sin_largo'
	/** La apertura cayó como incidente (rechazada/conflicto): la foto quedó sin dónde colgarse. */
	| 'entidad_rechazada';

export interface FallaFoto {
	codigo: number; // el status HTTP crudo (0 si no llegó a viajar): el dato técnico del encargado
	motivo: MotivoFallaFoto; // la clase, que gobierna el copy y si se puede reintentar
	en: string;
}

/** La foto del precinto, en el teléfono hasta que el servidor la acusa (§5.3). */
export interface FotoLocal {
	foto_id: string;
	entidad: 'llenado';
	/** La identidad del silo (`llenado_id`): la ruta del PUT. */
	entidad_id: string;
	/** La mutación `llenado.abrir` que crea la entidad: la foto sube cuando salió de la cola. */
	mutacion_id: string;
	blob: Blob;
	/** Calculado al capturar: el compare-and-delete tras el acuse lo compara DENTRO de la transacción. */
	sha256: string;
	capturada_en: string;
	/** false mientras el borrador no se confirmó: todavía puede recapturarse o descartarse. */
	confirmada: boolean;
	/**
	 * Falla TERMINAL de subida: deja de reintentarse y aparece en la bandeja del
	 * encargado. Un fallo TRANSITORIO (red, 5xx) no escribe nada: reescribir la
	 * fila re-serializaría el blob entero en cada intento.
	 */
	falla?: FallaFoto;
}

export interface CatalogosLocales {
	campanias: Array<{
		id: string;
		anio: number;
		letra: string | null;
		ultimo_nro_papel: number;
		corte_en: string | null;
		corte_responsable: string | null;
		fecha_inicio: string | null;
		fecha_fin: string | null;
	}>;
	/** v5: `tipo` y lo del batch de blendeo son opcionales en el tipo (un catálogo guardado antes no los trae): sin `tipo`, es de campo. */
	lotes_campo: Array<{ id: string; nombre: string; activo: number; tipo?: 'campo' | 'blendeo'; campania_id?: string | null; numero?: number | null; cerrado_en?: string | null }>;
	carros: Array<{ id: string; numero: number; color: string | null; activo: number }>;
	carros_tara: Array<{ id: string; carro_id: string; tara_g: number; vigente_desde: string }>;
	/** `lleva_descarte` (v4.2) es opcional en el tipo: un catálogo guardado antes no lo trae. */
	personas: Array<{ id: string; nombre: string; estacion_habitual: string | null; activo: number; lleva_descarte?: number }>;
	// v4 (§5.5): producción y calidad. Opcionales en el tipo para que un catálogo
	// guardado antes de la v4 siga cargando; la UI los trata como vacíos.
	hornos?: Array<{ id: string; nombre: string; activo: number }>;
	trieurs?: Array<{ id: string; numero: number; activo: number }>;
	silos?: Array<{ id: string; numero: number; activo: number }>;
	motivos_parada?: Array<{ id: string; nombre: string; orden: number; activo: number }>;
	defectos?: Array<{ id: string; grupo: 'material_extranio' | 'adventicio'; nombre: string; orden: number; activo: number }>;
	regla_calidad?: {
		id: string;
		version: number;
		algoritmo_version: number;
		polvo_q_max: number;
		polvo_p_max: number;
		densidad_min: number;
		densidad_max: number;
		humedad_min: number;
		humedad_max: number;
		adventicios_politica: 'requiere_decision' | 'L' | 'P';
		tolerancia_suma_tamices: number | null;
	} | null;
	/** Precintos ya usados en la campaña abierta: el número es único por campaña. */
	precintos_usados?: string[];
	/** v6 (§12.3): los carros cuya última llegada no tiene bajada después (solo en verde). */
	carros_en_playa?: Array<{ carro_id: string; nro_viaje: number | null; entrada_en: string }>;
	/** v6 (§12.8): los carros propios de descarte y sus taras (solo en verde). */
	carros_descarte?: Array<{ id: string; numero: number; color: string | null; activo: number }>;
	/**
	 * Verde reciente (§5.5, §12.11): los viajes y descartes de todos los teléfonos de verde en la ventana
	 * que fijó el servidor (solo en verde; `null` en las otras estaciones). Un catálogo guardado antes no
	 * trae la clave, y la pantalla vuelve a mostrar solo lo propio.
	 */
	verde_reciente?: import('../nucleo/verde-reciente').VerdeReciente | null;
	/**
	 * Producción reciente (§5.5, §12.20): los silos, pesadas, paradas y bajadas de todos los teléfonos de producción
	 * en la ventana que fijó el servidor (solo en producción; `null` en las otras estaciones). Un catálogo guardado
	 * antes no trae la clave, y la pantalla vuelve a mostrar solo lo propio.
	 */
	produccion_reciente?: import('../nucleo/produccion-reciente').ProduccionReciente | null;
	carros_descarte_tara?: Array<{ id: string; carro_descarte_id: string; tara_g: number; vigente_desde: string }>;
	/** v6 (§12.2): los avisos vigentes entre encargadas (solo en producción). */
	avisos_turno?: import('../nucleo/avisos').AvisoDelCatalogo[];
	/** v6 (§12.4): las notas de los silos que esta estación ve en sus listas. */
	notas_llenado?: import('../nucleo/avisos').NotaDelCatalogo[];
	/** v6 (§12.9): las muestras guardadas sin humedad (solo en calidad, que la completa). */
	muestras_sin_humedad?: import('../nucleo/muestra').MuestraSinHumedad[];
	/** §12.17: la humedad que entró en las últimas 24 h, para corregirla (solo calidad). */
	humedades_recientes?: import('../nucleo/muestra').HumedadesRecientes;
	/** §12.18: las hojas de las últimas 48 horas (solo calidad). */
	hojas_recientes?: import('../nucleo/muestra').HojasRecientes;
	/**
	 * v6 (§12.1): el último viaje de cada carro y su lote; producción lo muestra en la bajada. Desde el
	 * 6/10, `entrada_en` de esa llegada y `bajado` (el servidor ya conoce una bajada posterior): un
	 * catálogo guardado antes no los trae, y entonces el carro se muestra como pendiente.
	 */
	carros_ultimo_viaje?: import('../nucleo/produccion').CarroUltimoViaje[];
	/**
	 * 8/10 (§5.5, §12.1; issue #82): la última bajada de carro de la planta, con su lote (solo en
	 * producción; `null` sin bajadas). «¿De qué lote es?» propone ese lote. Un catálogo guardado antes no
	 * trae la clave, y entonces no se propone nada hasta el refresco.
	 */
	ultima_bajada?: import('../nucleo/produccion').UltimaBajada | null;
	/** Calidad: los silos cerrados sin muestra vigente, el más viejo primero (§5.5). */
	llenados_para_muestra?: Array<{
		llenado_id: string;
		campania_id: string;
		silo_fisico_id: string;
		lote_campo_id: string;
		precinto: string;
		cerrado_en: string;
		neto_g: number;
	}>;
	/** v5 (§5.5): los precintos con letra, listos para embolsar; lo ya envasado viene en gramos. */
	llenados_para_envasar?: Array<{
		llenado_id: string;
		campania_id: string;
		silo_fisico_id: string;
		lote_campo_id: string;
		precinto: string;
		cerrado_en: string;
		letra: 'Q' | 'P' | 'L' | 'descarte';
		muestra_id: string;
		/** v6 (§12.9): 1 si la muestra no tiene humedad: la letra es provisoria (se embolsa igual). Los catálogos viejos no lo traen. */
		falta_humedad?: number;
		/** Null en un silo sin pesada: sin neto no hay saldo ni tope (§5.7 r. 18). */
		neto_g: number | null;
		envasado_g: number;
		formato_previo: number | null;
	}>;
	/** v5: los cerrados sin muestra vigente y con neto —la misma lista que espera calidad—: candidatos a tarima. */
	llenados_para_tarima?: Array<{
		llenado_id: string;
		campania_id: string;
		silo_fisico_id: string;
		lote_campo_id: string;
		precinto: string;
		cerrado_en: string;
		neto_g: number;
		/** Lo ya envasado vigente del precinto (una tarima anulada conserva sus cargas) y el formato con que se embolsó, como en `llenados_para_envasar`. */
		envasado_g: number;
		formato_previo: number | null;
	}>;
	/** v5: los pallets vigentes en armado o en espera, con sus cargas y tarimas; la PWA los proyecta por identidad (§5.7 r. 21). */
	pallets_abiertos?: PalletAbiertoServidor[];
	/** §12.18: los pallets cerrados de las últimas 48 horas, de toda la estación (solo en envasado). Un catálogo guardado antes no lo trae. */
	pallets_cerrados_recientes?: import('../nucleo/embolsado').PalletsCerradosRecientes;
	/** v5, tercera versión (§5.7 r. 22): los rótulos emitidos sin usar ni anular de la campaña abierta; «pallet nuevo» elige uno. */
	rotulos_disponibles?: RotuloDelCatalogo[];
	llenados_abiertos?: Array<{
		llenado_id: string;
		version_id: string;
		campania_id: string;
		silo_fisico_id: string;
		horno_id: string;
		trieur_id: string;
		lote_campo_id: string;
		precinto: string;
		abierto_en: string;
		tara_base_g: number;
		ultimo_parcial_id: string | null;
		ultimo_bruto_g: number | null;
		ultimo_fin_en: string | null;
	}>;
}

let bdAbierta: Promise<IDBDatabase> | null = null;

export function abrirBd(): Promise<IDBDatabase> {
	bdAbierta ??= new Promise((resolver, rechazar) => {
		const pedido = indexedDB.open(NOMBRE_BD, VERSION_BD);
		pedido.onupgradeneeded = () => {
			const bd = pedido.result;
			// Idempotente por store: la versión 2 (14/9) suma `fotos` sobre bases que ya existen.
			const tiene = (nombre: string) => bd.objectStoreNames.contains(nombre);
			if (!tiene('config')) bd.createObjectStore('config');
			if (!tiene('catalogos')) bd.createObjectStore('catalogos');
			if (!tiene('outbox')) {
				const outbox = bd.createObjectStore('outbox', { keyPath: 'id' });
				outbox.createIndex('por_epoch_seq', ['epoch', 'seq'], { unique: true });
			}
			// Clave compuesta: la identidad contractual del recibo es (entidad, raíz).
			if (!tiene('recibos')) bd.createObjectStore('recibos', { keyPath: ['entidad', 'raiz_id'] });
			if (!tiene('borradores')) bd.createObjectStore('borradores', { keyPath: 'capture_id' });
			// La foto del precinto (§5.3): el blob vive acá hasta el acuse del servidor.
			if (!tiene('fotos')) bd.createObjectStore('fotos', { keyPath: 'foto_id' });
		};
		pedido.onsuccess = () => {
			const bd = pedido.result;
			// Otra pestaña con una app más nueva sube la versión de la base: esta conexión se cierra
			// para no bloquearla y la pestaña se recarga con el shell que corresponda (Codex, 14/9).
			bd.onversionchange = () => {
				bd.close();
				bdAbierta = null;
				recargarUnaVez();
			};
			resolver(bd);
		};
		pedido.onerror = () => {
			// Una app vieja contra una base ya migrada: recargar trae el shell nuevo si el SW lo tiene.
			if (pedido.error?.name === 'VersionError') recargarUnaVez();
			rechazar(pedido.error ?? new Error('no se pudo abrir la base local'));
		};
		// Bloqueada por una pestaña vieja: se espera, no se rechaza. Cuando esa pestaña cierre su
		// conexión (`onversionchange`), el upgrade sigue y `onsuccess` resuelve esta misma promesa.
		pedido.onblocked = () => console.warn('la base local está bloqueada por otra pestaña vieja: se espera a que cierre');
	});
	return bdAbierta;
}

/** Recarga la pestaña una sola vez por sesión: un shell viejo contra una base nueva no puede entrar en bucle. */
function recargarUnaVez(): void {
	if (typeof location === 'undefined' || typeof sessionStorage === 'undefined') return;
	try {
		if (sessionStorage.getItem('recarga-bd') === '1') return;
		sessionStorage.setItem('recarga-bd', '1');
	} catch {
		return;
	}
	location.reload();
}

/** Solo para tests: olvida la conexión cacheada (fake-indexeddb nuevo por test). */
export function reiniciarConexionBd(): void {
	bdAbierta = null;
}

export function promesa<T>(pedido: IDBRequest<T>): Promise<T> {
	return new Promise((resolver, rechazar) => {
		pedido.onsuccess = () => resolver(pedido.result);
		pedido.onerror = () => rechazar(pedido.error ?? new Error('fallo de IndexedDB'));
	});
}

export function alCompletar(tx: IDBTransaction): Promise<void> {
	return new Promise((resolver, rechazar) => {
		tx.oncomplete = () => resolver();
		tx.onerror = () => rechazar(tx.error ?? new Error('la transacción falló'));
		tx.onabort = () => rechazar(tx.error ?? new Error('la transacción se abortó'));
	});
}

export async function leerConfig<T>(bd: IDBDatabase, clave: 'sesion' | 'cola' | 'enrolamiento' | 'preferencias' | 'arranque' | 'avisos_entendidos'): Promise<T | undefined> {
	return promesa(bd.transaction('config').objectStore('config').get(clave) as IDBRequest<T | undefined>);
}

export async function escribirConfig(bd: IDBDatabase, clave: string, valor: unknown): Promise<void> {
	const tx = bd.transaction('config', 'readwrite');
	tx.objectStore('config').put(valor, clave);
	await alCompletar(tx);
}

export async function actualizarPreferencias(bd: IDBDatabase, cambios: Partial<Preferencias>): Promise<void> {
	const tx = bd.transaction('config', 'readwrite');
	const store = tx.objectStore('config');
	const actuales = ((await promesa(store.get('preferencias'))) as Preferencias | undefined) ?? {};
	store.put({ ...actuales, ...cambios }, 'preferencias');
	await alCompletar(tx);
}
